/**
 * Cart Validation Utilities
 * 
 * Validation logic for Add to Cart process.
 * Checks: product ID, imprint method, colors/sizes, location, text lines
 */

/**
 * Validate Add to Cart data
 * 
 * Performs comprehensive validation of all required data before adding to cart
 * 
 * @param {Object} stores - All required stores
 * @param {Object} stores.appStore - App store instance
 * @param {Object} stores.selectionStore - Selection store instance
 * @param {Object} stores.imprintStore - Imprint store instance
 * @param {Object} stores.newImprintStore - New imprint store instance
 * @returns {Object} Validation result { hasError: boolean, errors: string[] }
 */
export function validateAddToCart({ appStore, selectionStore, imprintStore, newImprintStore }) {
    const validationResult = {
        hasError: false,
        errors: []
    };
    
    // Step 1: Color/Size Selection validation
    // 1. Check main product ID
    if (!appStore.mainProductID) {
        validationResult.hasError = true;
        validationResult.errors.push('Step 1: Main product ID is missing');
    }
    
    // 2. Check imprint method is selected (Step 1)
    if (!imprintStore.selectedImprintMethod.sku) {
        validationResult.hasError = true;
        validationResult.errors.push('Step 1: Please select an imprint method');
    }
    
    // 3. Application type specific validation
    if (appStore.applicationType === 'default') {
        // 3a. Check color/size selections exist (Step 1)
        const imprintKey = imprintStore.selectedImprintMethod.name;
        const lines = selectionStore.getLinesByImprintKey(imprintKey);
        
        if (!lines || lines.length === 0) {
            validationResult.hasError = true;
            validationResult.errors.push('Step 1: Please select at least one color and size');
        }

        // 3b. Check minimum quantity requirements (Step 1)
        if (lines && lines.length > 0) {
            const totalQty = lines.reduce((sum, line) => sum + (line.totalQty || 0), 0);
            const minQty = imprintStore.currentMinQty || 0;

            if (minQty > 0 && totalQty < minQty) {
                validationResult.hasError = true;
                validationResult.errors.push(`Step 1: Minimum quantity requirements not met (minimum: ${minQty}, current: ${totalQty})`);
            }
        }
        
        // 3c. Check each line has at least one size selected (Step 1)
        if (lines) {
            lines.forEach((line, index) => {
                if (!line.selectedSizes || Object.keys(line.selectedSizes).length === 0) {
                    validationResult.hasError = true;
                    validationResult.errors.push(`Step 1: Line ${index + 1} - Please select at least one size`);
                }
            });
        }
        
        // Step 2: Customize/Imprint validation
        const currentImprint = newImprintStore.imprints[newImprintStore.currentImprintKey];
        if (currentImprint) {
            // Check location is selected (Step 2)
            if (!currentImprint.selectedLocation.sku) {
                validationResult.hasError = true;
                validationResult.errors.push('Step 2: Please select an imprint location');
            }
            
            // For "Start New Imprint" artwork method, validate text lines (Step 2)
            const artworkMethod = imprintStore.selectedArtworkMethod;
            if (artworkMethod === 'new') {
                const mt = currentImprint.messageType;

                // Validate message lines for text / text_image
                if (mt === 'text' || mt === 'text_image') {
                    const messageLines = (currentImprint.textLines || []).filter(l => l.type === 'message');

                    messageLines.forEach((line, index) => {
                        const hasText = !!(line.message && line.message.trim());
                        // selectedColor is the single source of truth:
                        // - standard color: file_name
                        // - pantone/custom: pantone string
                        const hasColor = !!line.selectedColor;

                        if (index === 0) {
                            // Line 1 is required
                            if (!hasText) {
                                validationResult.hasError = true;
                                validationResult.errors.push('Step 2: Missing text in Line 1');
                            }
                            if (!hasColor) {
                                validationResult.hasError = true;
                                validationResult.errors.push('Step 2: Missing color in Line 1');
                            }
                        } else {
                            // Lines 2-5 are optional:
                            // validate only if user partially filled the line
                            /*if (hasText && !hasColor) {
                                validationResult.hasError = true;
                                validationResult.errors.push(`Line ${index + 1}: Please select a color`);
                            }
                            if (hasColor && !hasText) {
                                validationResult.hasError = true;
                                validationResult.errors.push(`Line ${index + 1}: Please enter text`);
                            }*/
                        }
                    });
                }

                // Validate image for image / text_image (Step 2)
                if (mt === 'image' || mt === 'text_image') {
                    if (!currentImprint.imageData) {
                        validationResult.hasError = true;
                        validationResult.errors.push('Step 2: Please upload an image');
                    }
                    console.log("currentImprint");
                    console.log(currentImprint);

                    const imageLine = currentImprint.textLines.find((l) => l.type === "image");
                    if (imageLine && !imageLine.selectedColor) {
                        validationResult.errors.push('Step 2: Please select image color');
                    }

                }
            }
        }
    } else if (appStore.applicationType === 'calendar') {
        // Calendar products validation (Step 1)
        if (!appStore.calendar.productList || appStore.calendar.productList.length === 0) {
            validationResult.hasError = true;
            validationResult.errors.push('Step 1: Please select calendar products');
        }
    }
    
    return validationResult;
}

/**
 * Validate cart localStorage variables
 * 
 * Checks if required localStorage items exist for cart page
 * 
 * @returns {boolean} True if all required variables exist
 */
export function validateCartLocalStorageVariables() {
    try {
        const minQty = localStorage.getItem('minQty');
        return minQty !== null && minQty !== undefined;
    } catch (error) {
        console.error('[Validation] Failed to validate cart localStorage:', error);
        return false;
    }
}

/**
 * Set minimum quantity to localStorage
 * 
 * Saves the total quantity to localStorage for cart page validation
 * 
 * @param {string} groupId - Unique group identifier
 * @param {number} minQty - Minimum quantity required
 * @param {number} totalQty - Total quantity of items
 */
export function setMinQtyToStorage(groupId, minQty, totalQty) {
    try {
        // Get existing data from localStorage
        let minQtyStorage = {};
        const existingData = localStorage.getItem('minQty');

        if (existingData) {
            try {
                minQtyStorage = JSON.parse(existingData);
            } catch (parseError) {
                console.warn('[Validation] Failed to parse existing minQty data, creating new:', parseError);
                minQtyStorage = {};
            }
        }
        
        // Add or update the group data
        minQtyStorage[groupId] = {
            minQty: minQty,
            // errorMessage: false,
            totalQty: totalQty
        };

        // Save to localStorage
        localStorage.setItem('minQty', JSON.stringify(minQtyStorage));
        console.log('[Validation] Minimum quantity saved to localStorage for group:', groupId, {
            minQty,
            totalQty
        });
    } catch (error) {
        console.error('[Validation] Failed to save min qty to storage:', error);
    }
}
